import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickMutationApi } from "@/features/pathpick/api/mutation";
import { PathPickRequestsRequestBody } from "@/features/pathpick/types/request-body";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { refreshPathPickProducts } from "@/features/pathpick/utils/query-cache";

// 서버가 요청 접수(POST 응답) 후 신청 이력과 처리 상태를 비동기로 반영해,
// 즉시 재조회하면 탭 분류(request_type__in)와 상태 배지가 옛 값으로 내려올 수 있다.
// 반영분을 다시 가져오기 위한 지연 재조회 간격.
const DELAYED_REFETCH_MS = 3000;

// 다운로드/연동을 request_types로 한 번에 신청하는 일괄 요청 mutation
// 성공 안내 UI(완료 모달 등)는 호출부가 담당한다.
const useCreatePathPickRequests = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  // 실패 원인(한도 초과, 이미 연동됨 등)을 사용자가 바로 알아야 해 서버 문구를 함께 보여준다.
  const { error } = useAppToasts({ id: "GLOBAL", withServerMessage: true });

  return useMutation({
    mutationFn: (body: PathPickRequestsRequestBody) =>
      pathPickMutationApi.createPathPickRequests(companyId as number, body),
    onSuccess: (_data, variables) => {
      // 확정 수량 기반 KREAM 파일 신청만 주문에 흔적을 남긴다(주문 상세의 requestIds) —
      // 상품 기준 신청(연동/스마트스토어·KREAM 파일)은 주문과 무관해 주문 캐시를 건드리지 않는다.
      const isConfirmedQuantityRequest = variables.requestTypes.includes(
        "KREAM_EXCEL_CONFIRMED_QUANTITY"
      );

      const invalidate = () => {
        // 등록 신청 내역 카운트 갱신
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "requests", companyId],
        });
        // 크레딧(잔여 사용량) 소모 반영
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "usage", companyId],
        });
        // 상품의 채널별 요청 슬롯(requests.naverSync/naverExcel/kreamExcel) 배지 반영
        // — 화면에 없는 상품 찾기 목록은 비워, 돌아왔을 때 배지가 뒤늦게 붙지 않게 한다.
        refreshPathPickProducts(queryClient, companyId);
        // 전체 선택 대상(ids_only)과 "전체 n개 선택"의 n도 같은 변화를 따라간다.
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "api-product-ids", companyId],
        });
        // GNB/내역 페이지의 상품별 처리 내역 카운트(request.productCount) 반영
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "counts", companyId],
        });
        // 담은 상품 탭 배지 카운트 — 신청한 상품이 작업 전에서 등록 신청함으로 옮겨간다.
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "basket", "counts", companyId],
        });
        if (isConfirmedQuantityRequest) {
          // 주문 상세의 requestIds(신청 여부·신청 상품 내역 링크) 반영.
          // 상세 키가 ["pathpick","orders",companyId,orderId]로 목록과 프리픽스를 공유해
          // 이 한 번으로 목록과 상세가 함께 덮인다.
          queryClient.invalidateQueries({
            queryKey: ["pathpick", "orders", companyId],
          });
        }
      };
      invalidate();
      // 지연 재조회 — queryClient는 전역이라 컴포넌트 언마운트 후 실행돼도 안전하다.
      setTimeout(invalidate, DELAYED_REFETCH_MS);
    },
    onError: (err) => {
      error(err);
    },
  });
};

export default useCreatePathPickRequests;
